import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Collapsible, EmptyState, NoteCard } from "shared/components";
import { useNotesContext } from "shared/contextProviders";
import { css } from "styled-system/css";
import { Box, Grid } from "styled-system/jsx";
import { NotesContentProps } from "./NotesContent";
import { Icon, LinkButton } from "@ttc3k/trekker";
import { NavArrowDown, NavArrowUp } from "iconoir-react";
import _ from "lodash";

export type GridNotesViewProps = Pick<
  NotesContentProps,
  "isCreatingNote" | "setIsCreatingNote" | "forceSingleColumn"
>;

export const GridNotesView = ({
  isCreatingNote,
  setIsCreatingNote,
  forceSingleColumn,
}: GridNotesViewProps) => {
  const { notes, viewOnly } = useNotesContext();
  const { t } = useTranslation();

  const [showArchivedNotes, setShowArchivedNotes] = useState(false);

  const archivedNotes = useMemo(
    () => notes.filter(note => note.isArchived),
    [notes],
  );
  // Ensure the unArchivedNotes are ordered by date in descending order
  // The archived notes are already ordered in descending order and they are not
  // changeable, but the unArchived note's date is changeable so this logic is
  // needed.
  const unArchivedNotes = useMemo(
    () =>
      _.orderBy(
        notes.filter(note => !note.isArchived),
        [note => note.noteDateISO || note.createdDateISO],
        ["desc"],
      ),
    [notes],
  );

  const isSomeUnArchivedNotes = unArchivedNotes && unArchivedNotes.length > 0;
  return (
    <>
      <Grid
        gap={"300"}
        className={css({
          gridTemplateColumns:
            (isSomeUnArchivedNotes || isCreatingNote) && !forceSingleColumn
              ? {
                  base: "1fr",
                  xl: "repeat(2, 1fr)",
                }
              : "1fr",
        })}
      >
        {/* Creation of new note: */}
        {isCreatingNote && (
          <NoteCard
            initialMode="edit"
            size="lg"
            onSaveSuccess={() => setIsCreatingNote(false)}
            onCancel={() => setIsCreatingNote(false)}
          />
        )}

        {/* Unresolved notes */}
        {isSomeUnArchivedNotes ? (
          <>
            {unArchivedNotes.map(note => (
              <NoteCard
                note={note}
                key={note._id}
                initialMode="view"
                size="lg"
              />
            ))}
          </>
        ) : (
          <>
            {!isCreatingNote && (
              <EmptyState
                text={t("core:NOTES.EMPTY")}
                ctaButtonProps={
                  !viewOnly && {
                    children: t("core:NOTES.CREATE"),
                    size: "md",
                    onClick: () => setIsCreatingNote(true),
                  }
                }
                p={viewOnly ? "400" : "600"}
              />
            )}
          </>
        )}
      </Grid>

      {/* Resolved notes */}
      {archivedNotes && archivedNotes.length > 0 && (
        <>
          <Box>
            <LinkButton onClick={() => setShowArchivedNotes(prev => !prev)}>
              {showArchivedNotes
                ? t("core:NOTES.HIDE_ARCHIVED")
                : t("core:NOTES.SHOW_ARCHIVED")}
              &nbsp;
              <Icon Element={showArchivedNotes ? NavArrowUp : NavArrowDown} />
            </LinkButton>
          </Box>
          <Collapsible open={showArchivedNotes}>
            <Grid
              gap={"300"}
              gridTemplateColumns={{
                base: "1fr",
                xl: !forceSingleColumn ? "repeat(2, 1fr)" : "1fr",
              }}
            >
              {archivedNotes.map(note => (
                <NoteCard
                  note={note}
                  key={note._id}
                  initialMode="view"
                  size="lg"
                />
              ))}
            </Grid>
          </Collapsible>
        </>
      )}
    </>
  );
};
